'use client'

import { useState } from 'react'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Content } from '../useContentData'
import { CalendarDateType } from '../types/contentManagement'
import { getPlatformIcon, getPlatformLabel } from '@/lib/platform-utils'
import { useTimezone } from '@/hooks/useTimezone'

const contentTypeColors: Record<string, string> = {
  'ad_group': 'bg-blue-100 text-blue-800',
  'blog_post': 'bg-green-100 text-green-800',
  'email_newsletter': 'bg-purple-100 text-purple-800',
  'email_sales_sequence': 'bg-purple-100 text-purple-800',
  'email_educational_sequence': 'bg-purple-100 text-purple-800',
  'landing_page': 'bg-orange-100 text-orange-800',
  'quiz': 'bg-pink-100 text-pink-800',
  'social_post': 'bg-indigo-100 text-indigo-800',
  'tool': 'bg-yellow-100 text-yellow-800',
  'video': 'bg-red-100 text-red-800'
};

export interface ContentCalendarViewProps {
  // Content data
  contents: Content[];
  
  // Calendar state
  currentMonth: Date;
  onCurrentMonthChange: (date: Date) => void;
  dateType: CalendarDateType;
  onDateTypeChange: (type: CalendarDateType) => void;
  
  // Drag and drop state
  dragOverDay: number | null;
  onDragStart: (e: React.DragEvent, content: Content) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent, day: number) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, day: number) => void;
  isContentDraggable: (content: Content) => boolean;
  
  // Actions
  onViewEdit: (contentId: string) => void;
  onPlatformIconClick: (e: React.MouseEvent, platformKey: string | null) => void;
  
  // Utility functions
  formatDateInUserTimezone: (date: string, format: string) => string;
}

export function ContentCalendarView({
  contents,
  currentMonth,
  onCurrentMonthChange,
  dateType,
  onDateTypeChange,
  dragOverDay,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  isContentDraggable,
  onViewEdit,
  onPlatformIconClick,
  formatDateInUserTimezone
}: ContentCalendarViewProps) {
  const { timezone, isLoading } = useTimezone();

  // State for fisheye-lens hover effect
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  // Helper function to group contents by date for calendar view (timezone-aware)
  const getContentsByDate = (contents: Content[], year: number, month: number, dateType: CalendarDateType): Record<number, Content[]> => {
    const contentsByDate: Record<number, Content[]> = {};
    
    contents.forEach((content) => {
      // Get the appropriate date to display based on selected date type
      let displayDate: string | undefined;
      
      switch (dateType) {
        case 'scheduled_published':
          // Default behavior - prefer published_at, fallback to scheduled_time
          displayDate = content.published_at ?? content.scheduled_time ?? undefined;
          break;
        case 'created':
          displayDate = content.created_at;
          break;
        case 'published_only':
          displayDate = content.published_at ?? undefined;
          break;
        case 'scheduled_only':
          displayDate = content.scheduled_time ?? undefined;
          break;
        case 'held_only':
          // For held content, use scheduled_time (when it was supposed to be published)
          displayDate = (content.display_status === 'HOLD' || content.display_status === 'HELD') ? (content.scheduled_time ?? undefined) : undefined;
          break;
      }
      
      if (displayDate) {
        try {
          // Convert display date to user's timezone for calendar display
          const dateInUserTimezone = isLoading 
            ? new Date(displayDate)
            : (() => {
                // Use a proper timezone conversion approach
                const utcDate = new Date(displayDate);
                
                try {
                  // Get the year, month, and day in the target timezone using Intl.DateTimeFormat
                  const formatter = new Intl.DateTimeFormat('en-CA', {
                    timeZone: timezone || 'UTC',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  });
                  
                  const parts = formatter.formatToParts(utcDate);
                  const partsObj = parts.reduce((acc, part) => {
                    acc[part.type] = part.value;
                    return acc;
                  }, {} as Record<string, string>);
                  
                  // Validate that we have all required parts
                  if (!partsObj.year || !partsObj.month || !partsObj.day) {
                    throw new Error('Missing required date parts from formatter');
                  }
                  
                  // Create a date object with the timezone-adjusted values
                  const localizedDate = new Date(
                    parseInt(partsObj.year),
                    parseInt(partsObj.month) - 1, // Month is 0-based
                    parseInt(partsObj.day),
                    parseInt(partsObj.hour || '0'),
                    parseInt(partsObj.minute || '0'),
                    parseInt(partsObj.second || '0')
                  );
                  
                  return localizedDate;
                } catch (error) {
                  console.error(`Timezone conversion error for content ${content.name}:`, error);
                  // Fallback to UTC date
                  return utcDate;
                }
              })();
          
          if (dateInUserTimezone.getFullYear() === year && dateInUserTimezone.getMonth() === month) {
            const day = dateInUserTimezone.getDate();
            if (!contentsByDate[day]) {
              contentsByDate[day] = [];
            }
            contentsByDate[day].push(content);
          }
        } catch (error) {
          console.error('Error processing date for calendar:', error);
          // Fallback to UTC if timezone conversion fails
          const date = new Date(displayDate);
          if (date.getFullYear() === year && date.getMonth() === month) {
            const day = date.getDate();
            if (!contentsByDate[day]) {
              contentsByDate[day] = [];
            }
            contentsByDate[day].push(content);
          }
        }
      }
    });
    
    return contentsByDate;
  };

  // Get month details
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  
  // Group contents by day
  const contentsByDate = getContentsByDate(contents, year, month, dateType);
  
  // Generate days for previous month to fill the first row
  const daysFromPrevMonth = firstDayOfMonth;
  const prevMonthDays = [];
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  
  for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
    prevMonthDays.push(daysInPrevMonth - i);
  }
  
  // Format month name
  const monthName = currentMonth.toLocaleString('default', { month: 'long' });
  
  // Function to navigate to previous month
  const goToPrevMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    onCurrentMonthChange(newMonth);
  };
  
  // Function to navigate to next month
  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    onCurrentMonthChange(newMonth);
  };
  
  // Function to get today's date in user timezone
  const goToToday = () => {
    try {
      if (!isLoading && timezone) {
        // Get current date in user's timezone using the same approach as content dates
        const now = new Date();
        const formatter = new Intl.DateTimeFormat('en-CA', {
          timeZone: timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
        
        const parts = formatter.formatToParts(now);
        const partsObj = parts.reduce((acc, part) => {
          acc[part.type] = part.value;
          return acc;
        }, {} as Record<string, string>);
        
        const userTimezoneDate = new Date(
          parseInt(partsObj.year!),
          parseInt(partsObj.month!) - 1, // Month is 0-based
          parseInt(partsObj.day!)
        );
        
        onCurrentMonthChange(userTimezoneDate);
      } else {
        onCurrentMonthChange(new Date());
      }
    } catch (error) {
      console.error('Error getting today in user timezone:', error);
      onCurrentMonthChange(new Date());
    }
  };

  return (
    <div className="p-2">
      {/* Calendar Header */}
      <div className="mb-4 p-4 bg-white/80 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
              {monthName} {year}
            </h2>
            
            {/* Date Type Selector - Now in header */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium hidden sm:inline">Show:</span>
              <Select value={dateType} onValueChange={(value: CalendarDateType) => onDateTypeChange(value)}>
                <SelectTrigger className="w-[200px] h-9 bg-white/70 border-gray-200 hover:bg-white transition-colors text-sm">
                  <SelectValue placeholder="Select date type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled_published">Scheduled/Published/Held</SelectItem>
                  <SelectItem value="created">Created</SelectItem>
                  <SelectItem value="published_only">Published Only</SelectItem>
                  <SelectItem value="scheduled_only">Scheduled Only</SelectItem>
                  <SelectItem value="held_only">Held Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Month Navigation and View Toggle */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={goToPrevMonth}
              className="bg-white/70 hover:bg-white border-gray-200 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Previous Month</span>
              <span className="sm:hidden">Prev</span>
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={goToToday}
              className="bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700 transition-colors font-medium"
            >
              <Calendar className="h-4 w-4 mr-1" />
              Today
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={goToNextMonth}
              className="bg-white/70 hover:bg-white border-gray-200 transition-colors"
            >
              <span className="hidden sm:inline">Next Month</span>
              <span className="sm:hidden">Next</span>
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Legend - Dynamic based on date type */}
      <div className="flex items-center justify-center mb-4 p-2 bg-gray-50 rounded-lg">
        <div className="flex space-x-6 text-xs text-gray-600">
          {dateType === 'created' ? (
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span>Created content</span>
            </div>
          ) : dateType === 'published_only' ? (
            <div className="flex items-center">
              <span className="text-green-600 mr-1">✓</span>
              <div className="w-3 h-3 border-l-2 border-green-500 mr-2"></div>
              <span>Published</span>
            </div>
          ) : dateType === 'scheduled_only' ? (
            <div className="flex items-center">
              <span className="text-orange-600 mr-1">⏰</span>
              <div className="w-3 h-3 border-l-2 border-orange-500 mr-2"></div>
              <span>Scheduled</span>
            </div>
          ) : (
            <>
              <div className="flex items-center">
                <span className="text-green-600 mr-1">✓</span>
                <div className="w-3 h-3 border-l-2 border-green-500 mr-2"></div>
                <span>Published</span>
              </div>
              <div className="flex items-center">
                <span className="text-orange-600 mr-1">⏰</span>
                <div className="w-3 h-3 border-l-2 border-orange-500 mr-2"></div>
                <span>Scheduled</span>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 overflow-visible relative">
        {/* Weekday Headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
          <div key={i} className="text-center p-2 font-medium text-xs text-gray-500">
            {day}
          </div>
        ))}
        
        {/* Previous Month Days */}
        {prevMonthDays.map((day, i) => (
          <div key={`prev-${i}`} className="h-32 p-1 bg-gray-50 text-gray-400 border rounded text-xs">
            <div className="text-right">{day}</div>
          </div>
        ))}
        
        {/* Current Month Days */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          
          // Check if this is today in user's timezone
          const isToday = !isLoading && timezone && (() => {
            try {
              const now = new Date();
              const formatter = new Intl.DateTimeFormat('en-CA', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              });
              
              const parts = formatter.formatToParts(now);
              const partsObj = parts.reduce((acc, part) => {
                acc[part.type] = part.value;
                return acc;
              }, {} as Record<string, string>);
              
              const todayInUserTz = {
                year: parseInt(partsObj.year!),
                month: parseInt(partsObj.month!) - 1, // Month is 0-based
                day: parseInt(partsObj.day!)
              };
              
              return todayInUserTz.year === year && 
                     todayInUserTz.month === month && 
                     todayInUserTz.day === day;
            } catch (error) {
              console.error('Error checking if today:', error);
              // Fallback to basic date comparison
              const now = new Date();
              return now.getDate() === day && now.getMonth() === month && now.getFullYear() === year;
            }
          })();
          
          const dayContents = contentsByDate[day] || [];
          
          return (
            <div
              key={`current-${i}`}
              className={`relative h-32 p-1 border rounded overflow-y-auto transition-all duration-300 ease-in-out ${
                isToday ? 'bg-blue-50 border-blue-200' : 'bg-white'
              } ${
                dragOverDay === day ? 'bg-green-50 border-green-300 border-2' : ''
              } ${
                hoveredDay === day ? 'scale-150 shadow-2xl z-50 border-blue-400' : 'scale-100'
              }`}
              style={{
                transformOrigin: 'center center',
              }}
              onMouseEnter={() => setHoveredDay(day)}
              onMouseLeave={() => setHoveredDay(null)}
              onDragOver={(e) => onDragOver(e, day)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, day)}
            >
              <div
                className="transition-transform duration-300 ease-in-out"
                style={{
                  transform: hoveredDay === day ? 'scale(0.6667)' : 'scale(1)',
                  transformOrigin: 'top left',
                }}
              >
                <div className="text-right text-xs">{day}</div>
                <div className="mt-1 space-y-1">
                  {dayContents.map(content => {
                  // Determine display date and status based on date type
                  let displayTime: string | undefined;
                  let statusIndicator: string = '';
                  let statusLabel: string = '';
                  let borderColor: string = '';
                  
                  switch (dateType) {
                    case 'created':
                      displayTime = content.created_at;
                      statusIndicator = '📝';
                      statusLabel = 'Created';
                      borderColor = 'border-blue-500';
                      break;
                    case 'published_only':
                      displayTime = content.published_at ?? undefined;
                      statusIndicator = '✓';
                      statusLabel = 'Published';
                      borderColor = 'border-green-500';
                      break;
                    case 'scheduled_only':
                      displayTime = content.scheduled_time ?? undefined;
                      statusIndicator = '⏰';
                      statusLabel = 'Scheduled';
                      borderColor = 'border-orange-500';
                      break;
                    case 'held_only':
                      if (content.display_status === 'HOLD' || content.display_status === 'HELD') {
                        displayTime = content.scheduled_time ?? undefined;
                        statusIndicator = '⏸️';
                        statusLabel = 'Held';
                        borderColor = 'border-yellow-500';
                      } else {
                        displayTime = undefined;
                      }
                      break;
                    case 'scheduled_published':
                    default:
                      const isPublished = !!content.published_at;
                      const isHeld = content.display_status === 'HOLD' || content.display_status === 'HELD';
                      displayTime = content.published_at ?? content.scheduled_time ?? undefined;
                      if (isHeld) {
                        statusIndicator = '⏸️';
                        statusLabel = 'Held';
                        borderColor = 'border-yellow-500';
                      } else {
                        statusIndicator = isPublished ? '✓' : '⏰';
                        statusLabel = isPublished ? 'Published' : 'Scheduled';
                        borderColor = isPublished ? 'border-green-500' : 'border-orange-500';
                      }
                      break;
                  }
                  
                  const timeText = displayTime 
                    ? (isLoading 
                        ? new Date(displayTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                        : formatDateInUserTimezone(displayTime, 'h:mm a'))
                    : '';
                  
                  // Add visual indicators
                  const baseColors = contentTypeColors[content.content_type];
                  const statusModifier = '';
                  
                  const isDraggable = isContentDraggable(content)
                  
                  return (
                    <div 
                      key={content.id}
                      className={`text-xs p-1 rounded flex flex-col border-l-2 ${borderColor} ${baseColors} ${statusModifier} ${
                        isDraggable ? 'cursor-move hover:shadow-md transition-shadow' : 'cursor-pointer'
                      }`}
                      draggable={isDraggable}
                      onDragStart={isDraggable ? (e) => onDragStart(e, content) : undefined}
                      onDragEnd={isDraggable ? onDragEnd : undefined}
                      onClick={() => onViewEdit(content.id)}
                      title={`${content.name} - ${statusLabel}${timeText ? ` at ${timeText}` : ''}${
                        isDraggable ? ' (Drag to reschedule)' : ''
                      }`}
                    >
                      <div className="flex items-start">
                        <span className="text-[10px] mr-1" title={statusLabel}>{statusIndicator}</span>
                        {content.platform_key && (
                          <div 
                            className="inline-flex items-center hover:bg-white/20 rounded transition-colors cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              onPlatformIconClick(e, content.platform_key || null);
                            }}
                            title={`View ${getPlatformLabel(content.platform_key)} connection settings`}
                          >
                            {getPlatformIcon(content.platform_key, "h-3 w-3")}
                          </div>
                        )}
                        <span className="ml-1 truncate flex-1">{content.name}</span>
                      </div>
                      {displayTime && (
                        <div className="text-[10px] opacity-75 mt-1">
                          {timeText}
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>
            </div>
          );
        })}
        
        {/* Fill remaining grid cells if needed */}
        {Array.from({ length: (7 - ((firstDayOfMonth + daysInMonth) % 7)) % 7 }).map((_, i) => (
          <div key={`next-${i}`} className="h-32 p-1 bg-gray-50 text-gray-400 border rounded text-xs">
            <div className="text-right">{i + 1}</div>
          </div>
        ))}
      </div>
    </div>
  );
}